import React, { useState, FunctionComponent } from 'react'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { Link } from 'react-router-dom'
import axios from 'axios'
import classnames from 'classnames'
import LoaderButton from '../../components/loaderButton'
import { Tooltip } from '../../chassis/tooltip'
import HomeIcon from '../../icons/home'
import ContactInfoModal from './userVehicleListItem/ContactInfoModal'
import { updateUser, fetchUser } from './userActions'
import { displayNotification } from '../../actions/notificationsActions'
import { User, Address } from '../../core/types'

export interface QuoteHomeProtectionBtnProps {
  user?: User | null
  address?: Address | null
  variant?: 'button' | 'icon'
  className?: string
  disabled?: boolean
  disabledTooltip?: string
  isOrgEnabled?: boolean
  dispatch?: Dispatch<any>
}

export const QuoteHomeProtectionBtn: FunctionComponent<QuoteHomeProtectionBtnProps> = ({
  user,
  address,
  variant = 'button',
  className,
  disabled = false,
  disabledTooltip,
  isOrgEnabled,
  dispatch: propsDispatch,
}) => {
  let reduxDispatch: Dispatch<any> | null = null
  try {
    reduxDispatch = useDispatch()
  } catch (e) {
    reduxDispatch = null
  }
  const dispatch = propsDispatch || reduxDispatch

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  const [showContactModal, setShowContactModal] = useState(false)
  const [contactModalLoading, setContactModalLoading] = useState(false)
  const [contactModalError, setContactModalError] = useState<string | null>(null)

  // 1. Feature flag guard: per-org home_protection_handoff (fail closed
  // when an org is present without the flag). Unsaved applicants have no
  // user yet, so the disabled "save first" control still renders.
  const org = user?.organization
  const config = org?.configuration
  const resolvedFlag =
    isOrgEnabled !== undefined
      ? isOrgEnabled
      : config?.homeProtectionHandoff ??
        config?.home_protection_handoff ??
        org?.homeProtectionHandoff ??
        org?.home_protection_handoff ??
        user?.homeProtectionHandoff ??
        user?.home_protection_handoff
  const hasSavedUser = Boolean(user?.id)
  const hasOrgFlag =
    resolvedFlag !== undefined && resolvedFlag !== null
      ? Boolean(resolvedFlag)
      : !hasSavedUser

  if (!hasOrgFlag) {
    return null
  }

  // 2. Address resolution: user.currentAddress ?? user.addresses[0]
  const targetAddress =
    address ||
    user?.currentAddress ||
    (user?.addresses && user.addresses.length > 0 ? user.addresses[0] : null)
  const hasAddress = Boolean(
    targetAddress &&
      (targetAddress.line1 || targetAddress.city || targetAddress.zip)
  )

  // 3. Saved user status
  const isApplicantSaved = Boolean(user && user.id)

  // 4. Disabled determination
  const isDisabled =
    disabled || !isApplicantSaved || !hasAddress

  const postHandoff = async (userId: string, tab: Window | null) => {
    setIsLoading(true)
    setError(null)
    setErrorCode(null)

    try {
      const apiUrl = process.env.REACT_APP_BLINKER_API_URL || ''
      const response = await axios.post(
        `${apiUrl}/api/v3/admin/home_protection/handoff`,
        { user_id: userId }
      )

      const { redirect_url } = response.data
      if (tab) {
        tab.location.href = redirect_url
      } else {
        window.location.href = redirect_url
      }
    } catch (err: any) {
      if (tab) {
        try {
          tab.close()
        } catch (e) {}
      }

      const responseData = err?.response?.data
      const code = responseData?.error
      let message =
        responseData?.message ||
        err?.message ||
        'Failed to quote home protection'

      if (code === 'org_not_enabled') {
        message =
          "This organization doesn't have Home Protection enabled in Blinker 2.0"
      } else if (code === 'handoff_unavailable' || err?.response?.status === 502) {
        message = 'Home Protection is temporarily unavailable. Please try again.'
      } else if (code === 'no_address') {
        message = 'Add an address to quote home protection'
      } else if (code === 'unidentifiable_contact') {
        message = 'Email or phone is required'
      }

      setErrorCode(code)
      setError(message)

      if (
        dispatch &&
        (code === 'handoff_unavailable' || err?.response?.status === 502)
      ) {
        ;(dispatch as any)(displayNotification(message, 'error'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClick = () => {
    if (isDisabled || isLoading) return

    const hasPhone = Boolean(user?.phone && user.phone.trim().length > 0)
    const hasEmail = Boolean(user?.email && user.email.trim().length > 0)

    if (!hasPhone || !hasEmail) {
      setShowContactModal(true)
      return
    }

    // D5: Open blank tab synchronously before async handoff to avoid popup blockers
    const tab = window.open('about:blank')
    postHandoff(user!.id, tab)
  }

  const handleConfirmContact = (contactInfo: { phone: string; email: string }) => {
    setContactModalLoading(true)
    setContactModalError(null)

    // D5: Synchronously open tab on user click
    const tab = window.open('about:blank')

    if (!dispatch) {
      setContactModalLoading(false)
      setShowContactModal(false)
      return postHandoff(user!.id, tab)
    }

    return (dispatch as any)(
      updateUser({
        id: user!.id,
        phone: contactInfo.phone,
        email: contactInfo.email,
      })
    )
      .then(() => {
        setContactModalLoading(false)
        setShowContactModal(false)
        ;(dispatch as any)(displayNotification('Contact information updated'))
        if (user?.id) {
          ;(dispatch as any)(fetchUser(user.id))
        }
        return postHandoff(user!.id, tab)
      })
      .catch((err: any) => {
        if (tab) {
          try {
            tab.close()
          } catch (e) {}
        }
        setContactModalLoading(false)
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to update contact information'
        setContactModalError(errorMessage)
      })
  }

  const renderButtonContent = () => {
    if (variant === 'icon') {
      return (
        <button
          type="button"
          aria-label="Quote Home Protection"
          data-testid="quote-home-protection-icon-btn"
          className={classnames('quote-home-protection-icon-btn', className)}
          onClick={handleClick}
          disabled={isDisabled || isLoading}
          style={{
            background: 'none',
            border: 'none',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            padding: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          <HomeIcon
            color={isDisabled ? '#92a0ab' : '#01c0a0'}
            width="20px"
            height="20px"
          />
        </button>
      )
    }

    return (
      <LoaderButton
        type="button"
        data-testid="quote-home-protection-btn"
        className={classnames(
          'button-spacing button--full-width quote-home-protection-btn',
          className
        )}
        onClick={handleClick}
        disabled={isDisabled || isLoading}
        isLoading={isLoading}
        style={{
          marginTop: '8px',
          backgroundColor: isDisabled ? '#ccc' : undefined,
          color: isDisabled ? '#666' : undefined,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        Quote Home Protection
      </LoaderButton>
    )
  }

  const buttonElement = renderButtonContent()

  const renderedButton = isDisabled ? (
    <Tooltip
      triggerText={
        <span
          style={{
            display: 'inline-block',
            width: variant === 'button' ? '100%' : 'auto',
          }}
          data-testid="quote-home-protection-tooltip-trigger"
        >
          {buttonElement}
        </span>
      }
      theme="dark"
      contentWidth={280}
    >
      <p data-testid="quote-home-protection-tooltip-text">
        {!isApplicantSaved ? (
          'Save applicant first to quote home protection'
        ) : !hasAddress ? (
          <Link
            to={`/users/${user?.id}/address`}
            style={{ color: '#fff', textDecoration: 'underline' }}
          >
            Add an address to quote home protection
          </Link>
        ) : (
          disabledTooltip || 'Quote Home Protection'
        )}
      </p>
    </Tooltip>
  ) : (
    buttonElement
  )

  return (
    <>
      <ContactInfoModal
        isOpen={showContactModal}
        onClose={() => {
          setShowContactModal(false)
          setContactModalError(null)
        }}
        onConfirm={handleConfirmContact}
        description="Please update the customer's contact details to quote home protection."
        confirmButtonText="Confirm & Continue"
        phone={user?.phone}
        email={user?.email}
        showPhone={!user?.phone}
        showEmail={!user?.email}
        isLoading={contactModalLoading}
        serverError={contactModalError}
      />
      <div>
        {renderedButton}
        {error && (
          <div
            className={classnames('home-protection-error', {
              'home-protection-error--retryable':
                errorCode === 'handoff_unavailable',
            })}
            data-testid="home-protection-error"
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: '#d9534f',
              lineHeight: '1.4',
            }}
          >
            <span>{error}</span>
            {errorCode === 'handoff_unavailable' && (
              <button
                type="button"
                data-testid="home-protection-retry-button"
                onClick={() => {
                  if (user?.id) {
                    const tab = window.open('about:blank')
                    postHandoff(user.id, tab)
                  }
                }}
                style={{
                  marginLeft: '8px',
                  textDecoration: 'underline',
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '12px',
                }}
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default QuoteHomeProtectionBtn
